import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ConfirmationStep = ({ formData, content, onPublish, onSaveDraft, isPublishing }) => {
  const [publishOption, setPublishOption] = useState('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  const [enableAI, setEnableAI] = useState(true);
  const [allowComments, setAllowComments] = useState(true);
  const [trackProgress, setTrackProgress] = useState(true);

  const publishOptions = [
    { value: 'immediate', label: 'Publish Immediately' },
    { value: 'scheduled', label: 'Schedule for Later' }
  ];

  const validationChecklist = [
    { id: 'title', label: 'Journey title is set', checked: !!formData?.title },
    { id: 'image', label: 'Cover image uploaded', checked: !!formData?.image },
    { id: 'description', label: 'Description provided', checked: !!formData?.description },
    { id: 'classroom', label: 'Classroom assigned', checked: !!formData?.classroom },
    { id: 'content', label: 'Content added (minimum 1 item)', checked: content?.length > 0 }
  ];

  const allValid = validationChecklist?.every(item => item?.checked);

  const getContentTypeCounts = () => {
    const counts = {
      text: 0,
      image: 0,
      video: 0,
      quiz: 0,
      game: 0
    };
    content?.forEach(item => {
      counts[item.type] = (counts?.[item?.type] || 0) + 1;
    });
    return counts;
  };

  const contentCounts = getContentTypeCounts();

  const handlePublish = () => {
    if (allValid) {
      const publishData = {
        ...formData,
        content,
        settings: {
          publishOption,
          scheduledDate: publishOption === 'scheduled' ? scheduledDate : null,
          enableAI,
          allowComments,
          trackProgress
        },
        publishedAt: publishOption === 'immediate' ? new Date()?.toISOString() : null
      };
      onPublish(publishData);
    }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-sm">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-6">
          Review & Publish
        </h2>
        <p className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8">
          Review your learning trail before publishing to students
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-background rounded-lg p-4 md:p-6 border border-border">
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                Journey Overview
              </h3>
              
              <div className="space-y-4">
                {formData?.image && (
                  <div className="w-full h-48 md:h-64 rounded-lg overflow-hidden bg-muted">
                    <Image
                      src={formData?.image}
                      alt="Cover image for learning trail showing educational content"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div>
                  <h4 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
                    {formData?.title || 'Untitled Journey'}
                  </h4>
                  <p className="text-sm md:text-base text-muted-foreground">
                    {formData?.description || 'No description provided'}
                  </p>
                </div>

                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center">
                    <Icon name="Users" size={16} className="mr-2" />
                    <span>{formData?.classroom || 'No classroom'}</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name="FileText" size={16} className="mr-2" />
                    <span>{content?.length} content items</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 md:p-6 border border-border">
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                Content Summary
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(contentCounts)?.map(([type, count]) => (
                  count > 0 && (
                    <div key={type} className="p-4 bg-card rounded-lg border border-border">
                      <div className="flex items-center justify-between mb-2">
                        <Icon
                          name={
                            type === 'text' ? 'FileText' :
                            type === 'image' ? 'Image' :
                            type === 'video' ? 'Video' :
                            type === 'quiz'? 'HelpCircle' : 'Gamepad2'
                          }
                          size={20}
                          className="text-primary"
                        />
                        <span className="text-2xl font-bold text-foreground">{count}</span>
                      </div>
                      <p className="text-sm text-muted-foreground capitalize">{type}</p>
                    </div>
                  )
                ))}
              </div>
            </div>

            <div className="bg-background rounded-lg p-4 md:p-6 border border-border">
              <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
                Publishing Options
              </h3>
              
              <div className="space-y-4">
                <Select
                  label="Publish Schedule"
                  options={publishOptions}
                  value={publishOption}
                  onChange={setPublishOption}
                />

                {publishOption === 'scheduled' && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Schedule Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e?.target?.value)}
                      min={new Date()?.toISOString()?.slice(0, 16)}
                      className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-3 focus:ring-ring focus:ring-offset-3 focus:ring-offset-background transition-organic"
                    />
                  </div>
                )}

                <div className="space-y-3 pt-4 border-t border-border">
                  <h4 className="text-sm font-medium text-foreground mb-3">
                    Journey Settings
                  </h4>
                  
                  <Checkbox
                    label="Enable AI Assistant"
                    description="Students can ask questions to the AI bot"
                    checked={enableAI}
                    onChange={(e) => setEnableAI(e?.target?.checked)}
                  />

                  <Checkbox
                    label="Allow Student Comments"
                    description="Students can leave feedback and questions"
                    checked={allowComments}
                    onChange={(e) => setAllowComments(e?.target?.checked)}
                  />

                  <Checkbox
                    label="Track Progress"
                    description="Monitor student completion and performance"
                    checked={trackProgress}
                    onChange={(e) => setTrackProgress(e?.target?.checked)}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-background rounded-lg p-4 md:p-6 border border-border sticky top-24 space-y-6">
              <div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
                  Validation Checklist
                </h3>
                <div className="space-y-3">
                  {validationChecklist?.map((item) => (
                    <div key={item?.id} className="flex items-start">
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mr-3 mt-0.5 ${
                        item?.checked ? 'bg-success' : 'bg-muted'
                      }`}>
                        {item?.checked && (
                          <Icon name="Check" size={14} className="text-success-foreground" />
                        )}
                      </div>
                      <span className={`text-sm ${
                        item?.checked ? 'text-foreground' : 'text-muted-foreground'
                      }`}>
                        {item?.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {!allValid && (
                <div className="p-4 bg-warning/10 rounded-lg border border-warning/20">
                  <div className="flex items-start">
                    <Icon name="AlertTriangle" size={20} className="text-warning mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">
                        Incomplete Journey
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Complete all checklist items before publishing
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3 pt-4 border-t border-border">
                <Button
                  variant="default"
                  fullWidth
                  iconName={isPublishing ? undefined : "Send"}
                  onClick={handlePublish}
                  disabled={!allValid || (publishOption === 'scheduled' && !scheduledDate) || isPublishing}
                >
                  {isPublishing ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Publishing...
                    </span>
                  ) : (
                    publishOption === 'immediate' ? 'Publish Now' : 'Schedule Journey'
                  )}
                </Button>
                
                <Button
                  variant="outline"
                  fullWidth
                  iconName="Save"
                  onClick={onSaveDraft}
                >
                  Save as Draft
                </Button>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground text-center">
                  You can edit this journey anytime from your dashboard
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-success/5 rounded-lg p-4 md:p-6 border border-success/20">
        <div className="flex items-start">
          <Icon name="CheckCircle" size={24} className="text-success mr-3 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
              Ready to Publish?
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              Once published, students in the assigned classroom will be able to access this learning trail through QR codes. You can track their progress and engagement through the analytics dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationStep;